import { useMemo, useRef, useState, useEffect } from "react";
import {
  FileSignature,
  Heart,
  Pill,
  Smile,
  Plus,
  Trash2,
  Download,
  Send,
  Printer,
  Stethoscope,
  BadgeCheck,
  CircleDot,
  Search,
} from "lucide-react";
import { useDoctor } from "@/lib/doctor-store";
import { medicaments, type Medicament } from "@/lib/pharmacy-data";

type NoteType = "MALADIE" | "ALD" | "MEDICAMENTS" | "DENTAIRE";

const NOTE_TYPES: { id: NoteType; label: string; icon: typeof Heart; tint: string }[] = [
  { id: "MALADIE", label: "Feuille de Soins", icon: FileSignature, tint: "oklch(0.78 0.18 150)" },
  { id: "ALD", label: "ALD", icon: Heart, tint: "oklch(0.7 0.2 35)" },
  { id: "MEDICAMENTS", label: "Médicaments", icon: Pill, tint: "oklch(0.78 0.16 220)" },
  { id: "DENTAIRE", label: "Dentaire", icon: Smile, tint: "oklch(0.82 0.15 85)" },
];

type Acte = {
  date: string;
  code: string;
  lettre: string;
  cotation: string;
  valeur: number;
  montant: number;
  dents?: string;
};

const today = () => new Date().toISOString().slice(0, 10);

function Field({ label, children, full = false }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <label className={"block " + (full ? "sm:col-span-2" : "")}>
      <span className="mb-1.5 block text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

const ic =
  "w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none transition focus:border-success/60";

export default function ENotesView() {
  const [doctor] = useDoctor();
  const [type, setType] = useState<NoteType>("MALADIE");

  // Patient & assuré
  const [assure, setAssure] = useState({
    nom_prenom: "James Miamai",
    n_affiliation: "AF-2025-0142",
    n_immatriculation: "1024778",
    n_cnie: "JC125478",
    adresse: "Marrakech, Maroc",
  });
  const [beneficiaire, setBeneficiaire] = useState({
    lien: "ASSURE",
    nom_prenom: "James Miamai",
    date_naissance: "1989-04-12",
    sexe: "M",
  });

  const [actes, setActes] = useState<Acte[]>([
    { date: today(), code: "C", lettre: "C", cotation: "1", valeur: 150, montant: 150, dents: "" },
  ]);
  const [diagnostic, setDiagnostic] = useState({
    cim10: "I10",
    libelle: "Hypertension artérielle essentielle",
    arguments: "Patient suivi depuis 18 mois — bilan TA, ECG, biologie complète.",
  });
  const [meds, setMeds] = useState<{ cip?: string; nom: string; forme: string; dosage?: string; qte: number; ppv: number }[]>([
    { cip: "3400930000017", nom: "Doliprane", forme: "Comprimé", dosage: "500 mg", qte: 30, ppv: 12.5 },
  ]);

  const total = useMemo(() => {
    if (type === "MEDICAMENTS") return meds.reduce((s, m) => s + m.ppv * m.qte, 0);
    return actes.reduce((s, a) => s + a.montant, 0);
  }, [actes, meds, type]);

  const addActe = () =>
    setActes((a) => [
      ...a,
      { date: today(), code: "", lettre: "C", cotation: "1", valeur: 0, montant: 0, dents: "" },
    ]);
  const removeActe = (i: number) => setActes((a) => a.filter((_, k) => k !== i));
  const updateActe = (i: number, patch: Partial<Acte>) =>
    setActes((a) => a.map((x, k) => (k === i ? { ...x, ...patch } : x)));

  const isDental = type === "DENTAIRE";
  const isMeds = type === "MEDICAMENTS";
  const isALD = type === "ALD";

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_380px]">
      {/* MAIN */}
      <div className="space-y-5">
        {/* Type selector */}
        <div className="card-surface rounded-2xl p-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {NOTE_TYPES.map((t) => {
              const Icon = t.icon;
              const active = type === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setType(t.id)}
                  className={
                    "group flex items-center gap-3 rounded-xl px-3 py-3 text-sm transition " +
                    (active
                      ? "bg-foreground text-background"
                      : "bg-white/[0.03] text-muted-foreground hover:bg-white/[0.07] hover:text-foreground")
                  }
                >
                  <span
                    className="grid h-9 w-9 place-items-center rounded-lg"
                    style={{
                      background: active
                        ? "oklch(1 0 0 / 0.12)"
                        : `color-mix(in oklab, ${t.tint} 22%, transparent)`,
                      color: active ? "currentColor" : t.tint,
                    }}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-left">
                    <span className="block text-[10px] uppercase opacity-70">E-note</span>
                    <span className="block font-medium">{t.label}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Assuré + Bénéficiaire */}
        <div className="card-surface rounded-2xl p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm text-muted-foreground">Assuré & Bénéficiaire</h3>
            <span className="chip chip-active text-[10px]">CNOPS / CNSS</span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nom & Prénom de l'assuré">
              <input className={ic} value={assure.nom_prenom} onChange={(e) => setAssure({ ...assure, nom_prenom: e.target.value })} />
            </Field>
            <Field label="N° d'affiliation">
              <input className={ic} value={assure.n_affiliation} onChange={(e) => setAssure({ ...assure, n_affiliation: e.target.value })} />
            </Field>
            <Field label="N° d'immatriculation">
              <input className={ic} value={assure.n_immatriculation} onChange={(e) => setAssure({ ...assure, n_immatriculation: e.target.value })} />
            </Field>
            <Field label="CNIE">
              <input className={ic} value={assure.n_cnie} onChange={(e) => setAssure({ ...assure, n_cnie: e.target.value })} />
            </Field>
            <Field label="Adresse" full>
              <input className={ic} value={assure.adresse} onChange={(e) => setAssure({ ...assure, adresse: e.target.value })} />
            </Field>
          </div>

          <div className="my-5 h-px w-full bg-white/5" />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <Field label="Lien">
              <select className={ic} value={beneficiaire.lien} onChange={(e) => setBeneficiaire({ ...beneficiaire, lien: e.target.value })}>
                <option>ASSURE</option>
                <option>CONJOINT</option>
                <option>ENFANT</option>
              </select>
            </Field>
            <Field label="Nom & Prénom">
              <input className={ic} value={beneficiaire.nom_prenom} onChange={(e) => setBeneficiaire({ ...beneficiaire, nom_prenom: e.target.value })} />
            </Field>
            <Field label="Naissance">
              <input type="date" className={ic} value={beneficiaire.date_naissance} onChange={(e) => setBeneficiaire({ ...beneficiaire, date_naissance: e.target.value })} />
            </Field>
            <Field label="Sexe">
              <select className={ic} value={beneficiaire.sexe} onChange={(e) => setBeneficiaire({ ...beneficiaire, sexe: e.target.value })}>
                <option>M</option>
                <option>F</option>
              </select>
            </Field>
          </div>
        </div>

        {/* ALD specific */}
        {isALD && (
          <div className="card-surface rounded-2xl p-6">
            <h3 className="mb-4 text-sm text-muted-foreground">Diagnostic ALD</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label="CIM-10">
                <input className={ic} value={diagnostic.cim10} onChange={(e) => setDiagnostic({ ...diagnostic, cim10: e.target.value })} />
              </Field>
              <Field label="Libellé du diagnostic" full>
                <input className={ic} value={diagnostic.libelle} onChange={(e) => setDiagnostic({ ...diagnostic, libelle: e.target.value })} />
              </Field>
              <Field label="Arguments cliniques (≥ 50 caractères)" full>
                <textarea
                  rows={3}
                  className={ic}
                  value={diagnostic.arguments}
                  onChange={(e) => setDiagnostic({ ...diagnostic, arguments: e.target.value })}
                />
              </Field>
            </div>
          </div>
        )}

        {/* Médicaments */}
        {isMeds ? (
          <div className="card-surface rounded-2xl p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-sm text-muted-foreground">Médicaments prescrits</h3>
              <MedSearch
                presentCips={meds.map((m) => m.cip).filter(Boolean) as string[]}
                onPick={(med) =>
                  setMeds((prev) => {
                    const idx = prev.findIndex((x) => x.cip && x.cip === med.cip);
                    if (idx >= 0) {
                      const ok =
                        typeof window === "undefined"
                          ? true
                          : window.confirm(
                              `« ${med.nomCommercial} » est déjà prescrit. Fusionner et incrémenter la quantité ?`,
                            );
                      if (!ok) return prev;
                      return prev.map((x, k) => (k === idx ? { ...x, qte: x.qte + 1 } : x));
                    }
                    return [
                      ...prev,
                      {
                        cip: med.cip,
                        nom: med.nomCommercial,
                        forme: med.forme,
                        dosage: med.dosage,
                        qte: 1,
                        ppv: med.prixPublic,
                      },
                    ];
                  })
                }
              />
            </div>
            <div className="overflow-hidden rounded-xl border border-white/5">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">Nom commercial</th>
                    <th className="px-3 py-2 text-left">Forme</th>
                    <th className="px-3 py-2 text-left">Dosage</th>
                    <th className="px-3 py-2 text-right">Qté</th>
                    <th className="px-3 py-2 text-right">PPV (DH)</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {meds.map((m, i) => (
                    <tr key={(m.cip ?? "row") + "-" + i} className="border-t border-white/5">
                      <td className="px-2 py-1.5"><input className={ic} value={m.nom} onChange={(e) => setMeds((ms) => ms.map((x, k) => k === i ? { ...x, nom: e.target.value } : x))} /></td>
                      <td className="px-2 py-1.5"><input className={ic} value={m.forme} onChange={(e) => setMeds((ms) => ms.map((x, k) => k === i ? { ...x, forme: e.target.value } : x))} /></td>
                      <td className="px-2 py-1.5"><input className={ic} value={m.dosage ?? ""} onChange={(e) => setMeds((ms) => ms.map((x, k) => k === i ? { ...x, dosage: e.target.value } : x))} /></td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" onClick={() => setMeds((ms) => ms.map((x, k) => k === i ? { ...x, qte: Math.max(1, x.qte - 1) } : x))} className="grid h-7 w-7 place-items-center rounded-md bg-white/5 hover:bg-white/10">−</button>
                          <input type="number" min={1} className={ic + " w-16 text-right"} value={m.qte} onChange={(e) => setMeds((ms) => ms.map((x, k) => k === i ? { ...x, qte: Math.max(1, +e.target.value || 1) } : x))} />
                          <button type="button" onClick={() => setMeds((ms) => ms.map((x, k) => k === i ? { ...x, qte: x.qte + 1 } : x))} className="grid h-7 w-7 place-items-center rounded-md bg-white/5 hover:bg-white/10">+</button>
                        </div>
                      </td>
                      <td className="px-2 py-1.5"><input type="number" className={ic + " text-right"} value={m.ppv} onChange={(e) => setMeds((ms) => ms.map((x, k) => k === i ? { ...x, ppv: +e.target.value } : x))} /></td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-success">{(m.ppv * m.qte).toFixed(2)}</td>
                      <td className="px-2 py-1.5">
                        <button onClick={() => setMeds((ms) => ms.filter((_, k) => k !== i))} className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-accent/15 hover:text-accent"><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                  {meds.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-muted-foreground">Aucun médicament — utilisez la recherche pour en ajouter.</td></tr>
                  )}
                </tbody>
                {meds.length > 0 && (
                  <tfoot>
                    <tr className="border-t border-white/10 bg-white/[0.02]">
                      <td colSpan={5} className="px-3 py-2 text-right text-xs uppercase tracking-wider text-muted-foreground">Total PPV</td>
                      <td className="px-3 py-2 text-right text-sm font-medium tabular-nums text-success">{meds.reduce((s, m) => s + m.ppv * m.qte, 0).toFixed(2)} DH</td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        ) : (
          /* Actes (Maladie / ALD / Dentaire) */
          <div className="card-surface rounded-2xl p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm text-muted-foreground">
                {isDental ? "Actes dentaires" : "Actes & Cotation NGAP"}
              </h3>
              <button
                onClick={addActe}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10"
              >
                <Plus className="h-3.5 w-3.5" /> Ajouter un acte
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr className="border-b border-white/5">
                    <th className="px-2 py-2 text-left">Date</th>
                    <th className="px-2 py-2 text-left">Code</th>
                    <th className="px-2 py-2 text-left">Lettre clé</th>
                    <th className="px-2 py-2 text-left">Cotation</th>
                    {isDental && <th className="px-2 py-2 text-left">Dents (FDI)</th>}
                    <th className="px-2 py-2 text-right">Valeur</th>
                    <th className="px-2 py-2 text-right">Montant (DH)</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {actes.map((a, i) => (
                    <tr key={i} className="border-b border-white/5 last:border-0">
                      <td className="px-1 py-1.5"><input type="date" className={ic} value={a.date} onChange={(e) => updateActe(i, { date: e.target.value })} /></td>
                      <td className="px-1 py-1.5"><input className={ic} value={a.code} onChange={(e) => updateActe(i, { code: e.target.value })} /></td>
                      <td className="px-1 py-1.5">
                        <select className={ic} value={a.lettre} onChange={(e) => updateActe(i, { lettre: e.target.value })}>
                          <option>C</option><option>CS</option><option>K</option><option>KC</option><option>Z</option><option>B</option><option>D</option>
                        </select>
                      </td>
                      <td className="px-1 py-1.5"><input className={ic} value={a.cotation} onChange={(e) => updateActe(i, { cotation: e.target.value })} /></td>
                      {isDental && (
                        <td className="px-1 py-1.5">
                          <input placeholder="11, 21, 36" className={ic} value={a.dents || ""} onChange={(e) => updateActe(i, { dents: e.target.value })} />
                        </td>
                      )}
                      <td className="px-1 py-1.5"><input type="number" className={ic + " text-right"} value={a.valeur} onChange={(e) => updateActe(i, { valeur: +e.target.value })} /></td>
                      <td className="px-1 py-1.5"><input type="number" className={ic + " text-right"} value={a.montant} onChange={(e) => updateActe(i, { montant: +e.target.value })} /></td>
                      <td className="px-1 py-1.5">
                        <button onClick={() => removeActe(i)} className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-accent/15 hover:text-accent">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {isDental && (
              <DentalChart
                actes={actes}
                onToggleTooth={(rowIdx, tooth) => {
                  setActes((arr) =>
                    arr.map((a, k) => {
                      if (k !== rowIdx) return a;
                      const set = new Set(
                        (a.dents || "")
                          .split(/[,\s]+/)
                          .map((s) => s.trim())
                          .filter(Boolean),
                      );
                      set.has(tooth) ? set.delete(tooth) : set.add(tooth);
                      const ordered = Array.from(set).sort((x, y) => +x - +y);
                      return { ...a, dents: ordered.join(", ") };
                    }),
                  );
                }}
              />
            )}
          </div>
        )}

        {/* Total + actions */}
        <div className="card-surface flex flex-col items-stretch gap-4 rounded-2xl p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Total à facturer</div>
            <div className="text-3xl font-light tabular-nums">{total.toFixed(2)} <span className="text-base text-muted-foreground">DH</span></div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2 text-sm hover:bg-white/10"><Printer className="h-4 w-4" />Imprimer</button>
            <button className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2 text-sm hover:bg-white/10"><Download className="h-4 w-4" />PDF</button>
            <button className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background hover:opacity-90"><Send className="h-4 w-4" />Soumettre CNOPS</button>
          </div>
        </div>
      </div>

      {/* SIDEBAR — Doctor (auto-loaded from Configuration) */}
      <aside className="space-y-5">
        <div className="card-surface rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Médecin signataire
            </div>
            <span className="inline-flex items-center gap-1 text-[10px] text-success">
              <CircleDot className="h-3 w-3" /> Auto-chargé
            </span>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-gradient-to-br from-success/40 to-accent/40 text-sm font-medium">
              {doctor.prenom[0]}{doctor.nom[0]}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">Dr {doctor.prenom} {doctor.nom}</div>
              <div className="truncate text-xs text-muted-foreground">{doctor.specialite_principale}</div>
            </div>
          </div>

          <dl className="mt-5 space-y-2.5 text-xs">
            <div className="flex items-center justify-between gap-2 text-muted-foreground">
              <span>INPE</span><span className="text-foreground tabular-nums">{doctor.inpe || "—"}</span>
            </div>
            <div className="flex items-center justify-between gap-2 text-muted-foreground">
              <span>Autorisation</span><span className="text-foreground">{doctor.numero_autorisation || "—"}</span>
            </div>
            <div className="flex items-center justify-between gap-2 text-muted-foreground">
              <span>Cabinet</span><span className="text-foreground truncate max-w-[180px] text-right">{doctor.clinique_hopital}</span>
            </div>
            <div className="flex items-center justify-between gap-2 text-muted-foreground">
              <span>Ville</span><span className="text-foreground">{doctor.ville}</span>
            </div>
            <div className="flex items-center justify-between gap-2 text-muted-foreground">
              <span>Statut</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-success">
                <BadgeCheck className="h-3 w-3" />{doctor.statut}
              </span>
            </div>
          </dl>

          <p className="mt-5 text-[11px] text-muted-foreground">
            Modifiez ces paramètres dans <span className="text-foreground">Configuration</span>. La signature et le cachet sont injectés automatiquement à la soumission.
          </p>
        </div>

        <div className="card-surface rounded-2xl p-6">
          <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <Stethoscope className="h-3.5 w-3.5" /> Validation NGAP
          </div>
          <ul className="space-y-2.5 text-sm">
            <li className="flex items-center justify-between"><span className="text-muted-foreground">Cotation conforme</span><span className="text-success">✓</span></li>
            <li className="flex items-center justify-between"><span className="text-muted-foreground">Bénéficiaire</span><span className="text-success">✓</span></li>
            <li className="flex items-center justify-between"><span className="text-muted-foreground">Signature praticien</span><span className="text-success">✓</span></li>
            <li className="flex items-center justify-between"><span className="text-muted-foreground">Codes CIM-10</span><span className={isALD ? "text-success" : "text-muted-foreground"}>{isALD ? "✓" : "—"}</span></li>
          </ul>
        </div>
      </aside>
    </div>
  );
}

type MedField = "all" | "dci" | "cip" | "forme" | "marque";
const MED_FIELDS: { id: MedField; label: string }[] = [
  { id: "all", label: "Tout" },
  { id: "marque", label: "Marque" },
  { id: "dci", label: "DCI" },
  { id: "cip", label: "CIP" },
  { id: "forme", label: "Forme" },
];
const PAGE = 12;

function MedSearch({
  onPick,
  presentCips = [],
}: {
  onPick: (m: Medicament) => void;
  presentCips?: string[];
}) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [searching, setSearching] = useState(false);
  const [field, setField] = useState<MedField>("all");
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(PAGE);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (q === debounced) return;
    setSearching(true);
    const t = setTimeout(() => {
      setDebounced(q);
      setVisible(PAGE);
      setSearching(false);
    }, 220);
    return () => clearTimeout(t);
  }, [q, debounced]);

  const filtered = useMemo(() => {
    const needle = debounced.trim().toLowerCase();
    if (!needle) return medicaments;
    return medicaments.filter((m) => {
      const match = (s: string) => s.toLowerCase().includes(needle);
      switch (field) {
        case "dci": return match(m.dci);
        case "cip": return m.cip.includes(needle);
        case "forme": return match(m.forme);
        case "marque": return match(m.nomCommercial);
        default:
          return (
            match(m.nomCommercial) || match(m.dci) || match(m.forme) ||
            match(m.fabricant) || m.cip.includes(needle)
          );
      }
    });
  }, [debounced, field]);

  const shown = filtered.slice(0, visible);
  const hasMore = filtered.length > visible;

  const onScroll = () => {
    const el = listRef.current;
    if (!el || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      setVisible((v) => Math.min(filtered.length, v + PAGE));
    }
  };

  return (
    <div ref={wrapRef} className="relative w-80">
      <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 focus-within:border-success/60">
        <Search className="h-3.5 w-3.5 text-muted-foreground" />
        <input
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder="Rechercher un médicament…"
          className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
        />
        {searching ? (
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-success/40 border-t-success" />
        ) : (
          <Plus className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </div>

      {open && (
        <div className="absolute right-0 z-30 mt-1.5 w-[28rem] overflow-hidden rounded-xl border border-white/10 bg-card shadow-2xl">
          <div className="flex items-center gap-1 border-b border-white/5 p-1.5">
            {MED_FIELDS.map((f) => (
              <button
                key={f.id}
                onClick={() => { setField(f.id); setVisible(PAGE); }}
                className={
                  "rounded-full px-2.5 py-1 text-[10px] font-medium transition " +
                  (field === f.id
                    ? "bg-success/20 text-success ring-1 ring-success/40"
                    : "text-muted-foreground hover:bg-white/5 hover:text-foreground")
                }
              >
                {f.label}
              </button>
            ))}
            <span className="ml-auto pr-2 text-[10px] text-muted-foreground">
              {searching ? "Recherche…" : `${filtered.length} résultat${filtered.length > 1 ? "s" : ""}`}
            </span>
          </div>

          <div ref={listRef} onScroll={onScroll} className="max-h-80 overflow-y-auto p-1">
            {shown.map((m) => {
              const present = presentCips.includes(m.cip);
              return (
                <button
                  key={m.cip}
                  onClick={() => { onPick(m); setQ(""); setDebounced(""); setOpen(false); }}
                  className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-white/5"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-medium">{m.nomCommercial}</span>
                      <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-muted-foreground">{m.dosage}</span>
                      <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-muted-foreground">{m.forme}</span>
                      {present && (
                        <span className="rounded bg-[oklch(0.82_0.16_85)]/15 px-1.5 py-0.5 text-[10px] text-[oklch(0.85_0.15_85)] ring-1 ring-[oklch(0.82_0.16_85)]/30">
                          Déjà ajouté · fusionner
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                      {m.dci} · {m.fabricant} · CIP {m.cip} · Liste {m.liste}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-sm tabular-nums text-success">{m.prixPublic.toFixed(2)} DH</div>
                    <div className="text-[10px] text-muted-foreground">PPV</div>
                  </div>
                </button>
              );
            })}

            {!searching && shown.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                Aucun médicament trouvé{debounced ? ` pour « ${debounced} »` : ""}.
              </div>
            )}

            {hasMore && !searching && (
              <button
                onClick={() => setVisible((v) => Math.min(filtered.length, v + PAGE))}
                className="mt-1 w-full rounded-lg bg-white/[0.03] px-3 py-2 text-[11px] text-muted-foreground hover:bg-white/[0.06] hover:text-foreground"
              >
                Charger {Math.min(PAGE, filtered.length - visible)} de plus ({filtered.length - visible} restant{filtered.length - visible > 1 ? "s" : ""})
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const FDI_UPPER_R = [18, 17, 16, 15, 14, 13, 12, 11];
const FDI_UPPER_L = [21, 22, 23, 24, 25, 26, 27, 28];
const FDI_LOWER_L = [31, 32, 33, 34, 35, 36, 37, 38];
const FDI_LOWER_R = [48, 47, 46, 45, 44, 43, 42, 41];

const TOOTH_LABEL: Record<string, string> = {
  "1": "Incisive C.", "2": "Incisive L.", "3": "Canine",
  "4": "1ère prémol.", "5": "2e prémol.",
  "6": "1ère molaire", "7": "2e molaire", "8": "Dent de sagesse",
};

function toothName(fdi: number) {
  const pos = String(fdi % 10);
  const quadrant =
    fdi < 20 ? "maxillaire droit"
      : fdi < 30 ? "maxillaire gauche"
        : fdi < 40 ? "mandibule gauche"
          : "mandibule droit";
  return `${TOOTH_LABEL[pos] ?? "Dent"} · ${quadrant}`;
}

function DentalChart({
  actes,
  onToggleTooth,
}: {
  actes: Acte[];
  onToggleTooth: (rowIdx: number, tooth: string) => void;
}) {
  const [rowIdx, setRowIdx] = useState(0);
  const safeIdx = Math.min(rowIdx, Math.max(0, actes.length - 1));

  const currentTeeth = useMemo(() => {
    const a = actes[safeIdx];
    if (!a) return new Set<string>();
    return new Set(
      (a.dents || "")
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    );
  }, [actes, safeIdx]);

  const usedTeethByRow = useMemo(() => {
    const map = new Map<string, number[]>();
    actes.forEach((a, i) => {
      (a.dents || "")
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((t) => {
          const arr = map.get(t) ?? [];
          if (!arr.includes(i)) arr.push(i);
          map.set(t, arr);
        });
    });
    return map;
  }, [actes]);

  const Tooth = ({ n }: { n: number }) => {
    const k = String(n);
    const selected = currentTeeth.has(k);
    const usedByOther = (usedTeethByRow.get(k) ?? []).some((i) => i !== safeIdx);
    return (
      <button
        type="button"
        onClick={() => actes[safeIdx] && onToggleTooth(safeIdx, k)}
        title={`${n} — ${toothName(n)}`}
        className={
          "group relative grid h-11 w-9 place-items-center rounded-md text-[11px] font-medium ring-1 transition " +
          (selected
            ? "bg-success text-background ring-success shadow-[0_0_0_2px_oklch(0.7_0.16_150/0.25)]"
            : usedByOther
              ? "bg-[oklch(0.82_0.16_85)]/15 text-[oklch(0.85_0.15_85)] ring-[oklch(0.82_0.16_85)]/30 hover:bg-[oklch(0.82_0.16_85)]/25"
              : "bg-white/5 text-muted-foreground ring-white/10 hover:bg-success/15 hover:text-foreground")
        }
      >
        {n}
      </button>
    );
  };

  const Row = ({ teeth }: { teeth: number[] }) => (
    <div className="flex justify-center gap-1">
      {teeth.map((t) => <Tooth key={t} n={t} />)}
    </div>
  );

  return (
    <div className="mt-6 rounded-2xl bg-white/[0.03] p-4 ring-1 ring-white/5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Schéma dentaire (FDI)
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Cliquez sur une dent pour l'ajouter à la ligne d'acte sélectionnée.
          </div>
        </div>
        <label className="flex items-center gap-2 text-[11px]">
          <span className="text-muted-foreground">Ligne d'acte</span>
          <select
            className={ic + " w-auto py-1 text-xs"}
            value={safeIdx}
            onChange={(e) => setRowIdx(+e.target.value)}
            disabled={actes.length === 0}
          >
            {actes.map((a, i) => (
              <option key={i} value={i}>
                #{i + 1} — {a.code || "—"} {a.lettre}{a.cotation}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="space-y-3">
        <div>
          <div className="mb-1 flex justify-between px-1 text-[9px] uppercase tracking-wider text-muted-foreground">
            <span>Maxillaire droit (1x)</span><span>Maxillaire gauche (2x)</span>
          </div>
          <div className="flex justify-center gap-3">
            <Row teeth={FDI_UPPER_R} />
            <div className="w-px bg-white/10" />
            <Row teeth={FDI_UPPER_L} />
          </div>
        </div>

        <div className="mx-auto h-px w-3/4 bg-white/10" />

        <div>
          <div className="flex justify-center gap-3">
            <Row teeth={FDI_LOWER_R} />
            <div className="w-px bg-white/10" />
            <Row teeth={FDI_LOWER_L} />
          </div>
          <div className="mt-1 flex justify-between px-1 text-[9px] uppercase tracking-wider text-muted-foreground">
            <span>Mandibule droit (4x)</span><span>Mandibule gauche (3x)</span>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-success" /> Ligne sélectionnée</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[oklch(0.82_0.16_85)]/40 ring-1 ring-[oklch(0.82_0.16_85)]/30" /> Autre acte</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-white/10" /> Libre</span>
        {currentTeeth.size > 0 && (
          <span className="ml-auto text-foreground">
            {currentTeeth.size} dent{currentTeeth.size > 1 ? "s" : ""} — {Array.from(currentTeeth).sort((a, b) => +a - +b).join(", ")}
          </span>
        )}
      </div>
    </div>
  );
}
